import { useEffect, useRef, useState } from "react";
import Router from "next/router";
import styles from "./styles.module.scss";
import { useVisbilityState } from "../../../utils/pageVisibility";
import { splitStringToSpeak } from "../../../utils/splitStringToSpeak";
import PlayIcon from "../../../public/static-next/play.svg";
import PauseIcon from "../../../public/static-next/pause.svg";
import SettingsIcon from "../../../public/static-next/settings.svg";

interface ComponentTypes {
  title: string;
  content: string;
  slug: string;
}
interface VoicesTypes {
  default: boolean;
  lang: string;
  localService: boolean;
  name: string;
  voiceURI: string;
}
interface Utterance {
  lang: string;
  pitch: number;
  rate: number;
  text: string;
  voice: VoicesTypes[];
  volume: number;
}

export const SpeechToText = ({ title, content, slug }: ComponentTypes) => {
  const shortenStringWithoutCuttingWords = splitStringToSpeak(content);
  const [voices, setVoices] = useState<VoicesTypes[]>([]);
  const [supported, setSupported] = useState<boolean>(false);
  const [synth, setSynth] = useState(null);
  const [speaking, setSpeaking] = useState(0); // 0 = parado, 1 = falando, 2 = pausado
  const [text, setText] = useState(
    [title, shortenStringWithoutCuttingWords].flat(1)
  );
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [pitch, setPitch] = useState(1);
  const [rate, setRate] = useState(0.86);
  const stateRef = useRef<number>(2);
  const [openConfig, setOpenConfig] = useState(false);

  function handleEnd() {
    if (speaking === 2) {
      setCurrentTextIndex(currentTextIndex);
    }
    if (text[currentTextIndex] && stateRef.current !== 2) {
      setCurrentTextIndex(currentTextIndex + 1);
    }
    if (!text[currentTextIndex] && stateRef.current !== 2) {
      setSpeaking(0);
      setCurrentTextIndex(0);
    }
  }

  function say(texto: string) {
    if (texto) {
      // @ts-ignore
      const utterThis: Utterance = new SpeechSynthesisUtterance();
      utterThis.text = texto;
      utterThis.lang = "pt-BR";
      utterThis.voice = voices;
      utterThis.rate = rate;
      utterThis.pitch = pitch;
      synth.speak(utterThis);

      setSpeaking(1);
      stateRef.current = 1;
      // @ts-ignore
      utterThis.onend = handleEnd;
    }
  }

  useEffect(() => {
    if (speaking == 2) {
      stateRef.current = speaking;
      synth.cancel();
    }
  }, [speaking]);

  useEffect(() => {
    setText([title, shortenStringWithoutCuttingWords].flat(1));
    setCurrentTextIndex(0);
  }, [slug]);

  useEffect(() => {
    if (text[currentTextIndex] && speaking == 1) {
      say(text[currentTextIndex]);
    }
    if (!text[currentTextIndex]) {
      setSpeaking(0);
      setCurrentTextIndex(0);
    }
  }, [currentTextIndex]);

  function populateVoiceList() {
    setVoices(synth.getVoices());
    if (voices) {
      const PtBrVoice = synth
        .getVoices()
        .find(
          (voice: { lang: string }) =>
            voice.lang === "pt-BR" || voice.lang === "pt_BR"
        );

      setVoices(PtBrVoice);
      setSupported(true);
    } else {
      setSupported(false);
    }
  }

  useEffect(() => {
    if (synth) {
      populateVoiceList();
      if (speechSynthesis.onvoiceschanged !== undefined) {
        speechSynthesis.onvoiceschanged = populateVoiceList;
      }
    }
  }, [synth]);

  useEffect(() => {
    if (window.speechSynthesis) {
      setSynth(window.speechSynthesis);
    } else {
      setSupported(false);
    }
  }, [content]);

  const visibility = useVisbilityState();

  useEffect(() => {
    if (speaking) {
      if (visibility === "hidden") {
        setSpeaking(2);
      }
      window.onblur = function () {
        setSpeaking(2);
      };
    }
  }, [visibility, text, speaking]);

  useEffect(() => {
    if (supported && speaking === 1) {
      Router.events.on("routeChangeStart", () => setSpeaking(2));
    }

    return () => {
      if (supported && speaking === 1) {
        Router.events.off("routeChangeStart", () => setSpeaking(2));
      }
    };
  }, [supported, speaking]);

  return (
    supported && (
      <div className={styles.audioContainer}>
        <div className={styles.firstRow}>
          {(speaking === 0 || speaking === 2) && (
            <button
              className={styles.btnAudioPlayer}
              type="button"
              aria-label="Iniciar narração"
              onClick={() => {
                say(text[currentTextIndex]);
              }}
            >
              <PlayIcon width={30} height={30} title="Iniciar" />
            </button>
          )}
          {speaking === 1 && (
            <>
              <strong>{text[0]}</strong>
              <button
                className={styles.btnAudioPlayer}
                type="button"
                aria-label="Pausar narração"
                onClick={() => {
                  setSpeaking(2);
                }}
              >
                <PauseIcon width={30} height={30} title="Pausar" />
              </button>
            </>
          )}

          <div>
            <span>
              <p>Escute a notícia.</p>
              <p>Ative a leitura do texto</p>
            </span>
          </div>

          <button
            className={styles.btnConfigs}
            type="button"
            aria-label="Configurações do leitor"
            onClick={() => {
              openConfig ? setOpenConfig(false) : setOpenConfig(true);
            }}
          >
            <SettingsIcon width={30} height={30} title="Configurações" />
          </button>
        </div>

        {openConfig && (
          <div className={styles.secondRow}>
            <div>
              <span>Velocidade</span>
              <input
                type="range"
                min="0.5"
                max="1.5"
                defaultValue="0.86"
                step="0.01"
                id="rate"
                onChange={(event) => {
                  setRate(Number(event.target.value));
                }}
              />
              <output style={{ width: 32 }}>{rate}</output>
            </div>

            <div>
              <span>Tom da voz</span>
              <input
                type="range"
                min="0"
                max="1.5"
                defaultValue="1"
                step="0.1"
                id="pitch"
                onChange={(event) => {
                  setPitch(Number(event.target.value));
                }}
              />
              <output style={{ width: 32 }}>{pitch}</output>
            </div>
          </div>
        )}
      </div>
    )
  );
};
